import setDynamicFormFieldValue from "@iewcasia/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@iewcasia/Store/Reducers";
import { DynamicFormContext } from "@iewcasia/Widgets/DynamicForm/DynamicForm";
import DynamicFormFieldShell from "@iewcasia/Widgets/DynamicForm/DynamicFormFieldShell";
import { useRegisterDynamicFormFieldKeys } from "@iewcasia/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextArea from "@insite/mobius/TextArea";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    fieldKey = "fieldKey",
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
    maxLength = "maxLength",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldKey]: string;
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
        [fields.maxLength]: number | null;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const fieldKey = ownProps.fields.fieldKey || "Description";
    return {
        fieldKey,
        fieldValue: state.customDynamicForm?.fieldValues[fieldKey],
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

/** Salesforce Dynamic Form textarea field widget. */
const DynamicMessage: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldKey,
    fieldValue,
    setDynamicFormFieldValue,
}) => {
    const { label, hintText, isRequired, maxLength } = widgetFields;
    const formContext = React.useContext(DynamicFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    const resolvedMaxLength = maxLength && maxLength > 0 ? maxLength : undefined;
    useRegisterDynamicFormFieldKeys(id, [fieldKey]);

    const validate = React.useCallback(() => {
        const value = fieldValue == null ? "" : String(fieldValue);
        let error = "";
        if (isRequired && !value.trim()) {
            error = siteMessage("Field_Required", label) as string;
        } else if (resolvedMaxLength && value.length > resolvedMaxLength) {
            error = translate("{0} cannot exceed {1} characters", label, String(resolvedMaxLength));
        }
        setErrorMessage(error);
        return !error;
    }, [fieldValue, isRequired, label, resolvedMaxLength]);

    React.useEffect(() => {
        formContext.validators[fieldKey] = validate;
        if (typeof fieldValue !== "undefined") {
            validate();
        }
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, fieldValue, validate, formContext.validators]);

    return (
        <DynamicFormFieldShell>
            <TextArea
                label={label}
                required={isRequired}
                placeholder={hintText}
                value={fieldValue || ""}
                maxLength={resolvedMaxLength}
                error={errorMessage}
                rows={5}
                mergeCss={true}
                css={css`
                    width: 100%;
                    min-height: 120px;
                    resize: vertical;
                    padding-top: 10px;
                    padding-bottom: 10px;
                    box-sizing: border-box;
                `}
                onChange={event => setDynamicFormFieldValue({ key: fieldKey, value: event.currentTarget.value })}
            />
        </DynamicFormFieldShell>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicMessage),
    definition: {
        group: widgetGroup,
        displayName: "Comments",
        icon: "comment",
        fieldDefinitions: [
            {
                name: fields.fieldKey,
                displayName: "Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.maxLength,
                displayName: "Max Length",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: null,
                min: 1,
                placeholder: "No limit",
            },
        ],
    },
};

export default widgetModule;
